/**
 * 电商套图 · 左侧常驻设置栏（三阶段共用）
 *
 * 区块：商品 / 市场与创作 / 核心卖点（含 AI 帮写）/ 出图参数（项目级生成默认值）。
 * 表单草稿由父组件持有，保存走 PATCH /projects/:id。
 */
import { useState } from 'react';
import { ChevronDown, Loader2, Sparkles } from 'lucide-react';
import { suiteTheme } from '../theme';
import type { SuiteProjectDetail, SuiteProtectionMode } from '../api';

export interface SuiteGenParams {
  model: string;
  size: string;
  quality: string;
}

export interface SuiteProjectDraft {
  title: string;
  productName: string;
  productCategory: string;
  productDescription: string;
  verifiedFacts: string;
  prohibitedClaims: string;
  targetPlatform: string;
  targetMarket: string;
  copyLanguage: string;
  defaultProtection: SuiteProtectionMode;
}

const PLATFORM_OPTIONS = ['大陆电商（淘宝/天猫）', '抖音电商', '小红书', '京东', '拼多多', '亚马逊', '独立站'];
const CATEGORY_OPTIONS = ['美妆', '服装', '食品', '数码', '家居', '珠宝配饰', '母婴', '运动户外', '其他'];

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div className={suiteTheme.sidebarSection}>
      <button type="button" className={suiteTheme.sidebarHeader} onClick={() => setOpen((prev) => !prev)}>
        {title}
        <ChevronDown className={`h-4 w-4 text-zinc-500 transition ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="space-y-3 border-t border-white/5 px-4 py-4">{children}</div>}
    </div>
  );
}

export function SetupSidebar({
  project,
  draft,
  onDraftChange,
  onSave,
  saving,
  onAssistDescription,
  assisting,
  models,
  genParams,
  onGenParamsChange,
}: {
  project: SuiteProjectDetail;
  draft: SuiteProjectDraft;
  onDraftChange: (patch: Partial<SuiteProjectDraft>) => void;
  onSave: () => void;
  saving: boolean;
  onAssistDescription: () => void;
  assisting: boolean;
  models: Array<{ id: string; name: string }>;
  genParams: SuiteGenParams;
  onGenParamsChange: (patch: Partial<SuiteGenParams>) => void;
}) {
  return (
    <div className="flex w-full flex-col gap-3 lg:w-[300px] lg:shrink-0">
      <Section title="商品">
        <div>
          <label className={suiteTheme.label}>项目名称</label>
          <input
            className={suiteTheme.input}
            value={draft.title}
            onChange={(e) => onDraftChange({ title: e.target.value })}
            placeholder="例如：夏季白T 套图"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={suiteTheme.label}>商品名称</label>
            <input
              className={suiteTheme.input}
              value={draft.productName}
              onChange={(e) => onDraftChange({ productName: e.target.value })}
              placeholder="商品名"
            />
          </div>
          <div>
            <label className={suiteTheme.label}>品类</label>
            <select
              className={suiteTheme.select}
              value={draft.productCategory}
              onChange={(e) => onDraftChange({ productCategory: e.target.value })}
            >
              <option value="">请选择</option>
              {CATEGORY_OPTIONS.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </div>
        </div>
      </Section>

      <Section title="市场与创作">
        <div>
          <label className={suiteTheme.label}>目标平台</label>
          <select
            className={suiteTheme.select}
            value={draft.targetPlatform}
            onChange={(e) => onDraftChange({ targetPlatform: e.target.value })}
          >
            <option value="">请选择</option>
            {PLATFORM_OPTIONS.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={suiteTheme.label}>目标市场</label>
          <input
            className={suiteTheme.input}
            value={draft.targetMarket}
            onChange={(e) => onDraftChange({ targetMarket: e.target.value })}
            placeholder="如：国内 / 东南亚"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={suiteTheme.label}>文案语种</label>
            <input
              className={suiteTheme.input}
              value={draft.copyLanguage}
              onChange={(e) => onDraftChange({ copyLanguage: e.target.value })}
              placeholder="中文"
            />
          </div>
          <div>
            <label className={suiteTheme.label}>默认保护</label>
            <select
              className={suiteTheme.select}
              value={draft.defaultProtection}
              onChange={(e) => onDraftChange({ defaultProtection: e.target.value as SuiteProtectionMode })}
            >
              <option value="PIXEL_PROTECTED">像素保护</option>
              <option value="CREATIVE">创意</option>
            </select>
          </div>
        </div>
      </Section>

      <Section title="核心卖点">
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="text-xs font-medium text-zinc-500">商品描述</label>
            <button
              type="button"
              onClick={onAssistDescription}
              disabled={assisting}
              className="inline-flex items-center gap-1 rounded-full border border-blue-300/60 bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-600 transition hover:bg-blue-400 disabled:opacity-50"
            >
              {assisting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              AI 帮写
            </button>
          </div>
          <textarea
            className={`${suiteTheme.textarea} min-h-[120px]`}
            value={draft.productDescription}
            onChange={(e) => onDraftChange({ productDescription: e.target.value })}
            placeholder={'产品名称：...\n核心卖点：\n- ...'}
          />
        </div>
        <div>
          <label className={suiteTheme.label}>已核实事实（每行一条）</label>
          <textarea
            className={`${suiteTheme.textarea} min-h-[72px]`}
            value={draft.verifiedFacts}
            onChange={(e) => onDraftChange({ verifiedFacts: e.target.value })}
            placeholder={'每行一条，例如：续航 8 小时'}
          />
        </div>
        <div>
          <label className={suiteTheme.label}>禁止宣称（每行一条）</label>
          <textarea
            className={`${suiteTheme.textarea} min-h-[72px]`}
            value={draft.prohibitedClaims}
            onChange={(e) => onDraftChange({ prohibitedClaims: e.target.value })}
            placeholder={'每行一条，例如：医用级'}
          />
        </div>
      </Section>

      <Section title="出图参数">
        <div>
          <label className={suiteTheme.label}>生图模型</label>
          <select
            className={suiteTheme.select}
            value={genParams.model}
            onChange={(e) => onGenParamsChange({ model: e.target.value })}
          >
            <option value="">自动选择</option>
            {models.map((model) => (
              <option key={model.id} value={model.id}>{model.name}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={suiteTheme.label}>尺寸</label>
            <select
              className={suiteTheme.select}
              value={genParams.size}
              onChange={(e) => onGenParamsChange({ size: e.target.value })}
            >
              <option value="1024x1024">1024×1024 主图</option>
              <option value="800x800">800×800 主图</option>
              <option value="1000x1334">1000×1334 详情竖版</option>
              <option value="1200x800">1200×800 横版</option>
              <option value="auto">自适应</option>
            </select>
          </div>
          <div>
            <label className={suiteTheme.label}>画质</label>
            <select
              className={suiteTheme.select}
              value={genParams.quality}
              onChange={(e) => onGenParamsChange({ quality: e.target.value })}
            >
              <option value="medium">高清</option>
              <option value="low">标准</option>
              <option value="high">超清</option>
            </select>
          </div>
        </div>
        <p className="text-xs leading-relaxed text-zinc-500">
          出图参数作为确认生成时的默认值；单品可在分镜检查器中单独调整。
        </p>
      </Section>

      <button
        type="button"
        className={`${suiteTheme.primaryBtn} sticky bottom-4 mt-1 w-full`}
        onClick={onSave}
        disabled={saving}
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
        保存项目设置
      </button>
      <p className="pb-2 text-center text-xs text-zinc-600">
        项目 {project.status === 'draft' ? '尚未规划' : `已规划 · ${project.shots.length} 个分镜`}
      </p>
    </div>
  );
}
